/**
 * Coque de l'écran carte : boutons flottants et panneaux superposés.
 *
 * Reproduit la disposition de `activity_main.xml` et le comportement des
 * contrôleurs de HUD (`HudController`, `LiveHudController`,
 * `MarkerAlertHudController`).
 */

import { canvasPixelRatio } from './canvasScale'
import { createIcon, type IconName } from './icons'
import type { Translator } from '../i18n'
import type { RoadbookEntry } from '../core/track'
import {
  fitKmIndicatorFontSize,
  hudTextSize,
  KM_INDICATOR_FONT_SIZES,
  markerAlertSize,
} from '../core/preferences'

export interface ShellCallbacks {
  onMenu(): void
  onMyLocation(): void
  onGuidance(): void
  onSatellite(): void
  onZoomIn(): void
  onZoomOut(): void
  onCompass(): void
  onKmIndicator(): void
}

function button(
  icon: IconName,
  label: string,
  className: string,
  onClick: () => void,
): HTMLButtonElement {
  const element = document.createElement('button')
  element.type = 'button'
  element.className = className
  element.title = label
  element.setAttribute('aria-label', label)
  element.append(createIcon(icon))
  element.addEventListener('click', onClick)
  return element
}

/**
 * Jeu laissé entre le profil d'altitude et ses voisins.
 *
 * Repris de l'écart mesuré sur l'APK : une trentaine de pixels d'appareil, soit
 * une dizaine de points.
 */
const PROFILE_GAP = 10

/**
 * Jeu laissé entre l'indicateur kilométrique et les boutons qui l'encadrent.
 *
 * Repris du `32 * density` qu'`applyKmIndicatorStyle` retranche de la largeur
 * d'écran, en plus des deux boutons : seize points de chaque côté.
 */
const KM_INDICATOR_CLEARANCE = 32

/**
 * Kilomètre affiché : **tronqué** à la décimale, jamais arrondi.
 *
 * Android tronque partout (`LiveHudController.formatKmTruncated` : plancher
 * au-dessus de zéro, plafond en dessous), et la différence se voit — 12,46 km
 * s'écrit « 12,4 » là-bas et s'écrivait « 12,5 » ici. Les deux panneaux du
 * portage se contredisaient d'ailleurs entre eux sur la même valeur :
 * l'indicateur kilométrique tronquait, le roadbook arrondissait. Une seule
 * fonction pour les deux, faute de quoi la divergence revient.
 */
export function truncateKm(value: number): number {
  return value >= 0 ? Math.floor(value * 10) / 10 : Math.ceil(value * 10) / 10
}

export class Shell {
  readonly root: HTMLElement

  readonly fabMenu: HTMLButtonElement
  readonly fabMyLocation: HTMLButtonElement
  readonly fabGuidance: HTMLButtonElement
  readonly fabSatellite: HTMLButtonElement
  readonly zoomButtons: HTMLElement

  readonly compass: HTMLButtonElement
  readonly #compassDial: SVGSVGElement

  readonly speedHud: HTMLElement
  readonly #speedValue: HTMLElement
  readonly #altitudeValue: HTMLElement

  readonly kmIndicator: HTMLButtonElement
  readonly roadbook: HTMLElement
  readonly profileHud: HTMLElement
  readonly #profileCanvas: HTMLCanvasElement
  readonly alertHud: HTMLElement
  readonly #alertCanvas: HTMLCanvasElement

  readonly #scaleBar: HTMLElement
  readonly #scaleLabel: HTMLElement
  readonly #scaleTrack: HTMLElement

  #snackbar: HTMLElement | null = null
  #lastCompassBearing = Number.NaN

  /**
   * Dernière image posée dans le profil d'altitude.
   *
   * Conservée pour pouvoir la retracer quand la largeur du panneau change :
   * une largeur nouvelle sans redessin laisse le navigateur étirer le bitmap
   * précédent, et la courbe ment — mauvaise échelle horizontale, curseur
   * décalé — jusqu'au relevé suivant, cinq à dix secondes plus tard.
   */
  #profileFrame: {
    distances: readonly number[]
    elevations: readonly number[]
    windowStart: number
    windowEnd: number
    cursorFraction: number
  } | null = null

  /**
   * Dernière image posée dans le radar d'alerte.
   *
   * Même raison que pour le profil, avec une conséquence plus visible : la
   * barre est haute et étroite, si bien qu'un passage en plein écran ou une
   * rotation change surtout sa hauteur. Le navigateur étire alors le bitmap
   * précédent sur la nouvelle boîte, et les losanges des marqueurs — seuls
   * dessins carrés du panneau — s'allongent ou s'écrasent visiblement.
   */
  #alertFrame: {
    entries: readonly RoadbookEntry[]
    alertRadiusKm: number
    sizeLevel: number
  } | null = null

  constructor(
    container: HTMLElement,
    private readonly t: Translator,
    callbacks: ShellCallbacks,
  ) {
    this.root = container

    const scrim = document.createElement('div')
    scrim.className = 'status-bar-scrim'
    container.append(scrim)

    // --- Boutons ---
    this.fabMenu = button('menu', t.t('menu'), 'fab fab--primary', callbacks.onMenu)
    this.fabMenu.classList.add('anchor-top-start')
    container.append(this.fabMenu)

    this.fabMyLocation = button(
      'myLocation',
      t.t('my_location'),
      'fab',
      callbacks.onMyLocation,
    )
    this.fabMyLocation.classList.add('anchor-top-end')
    this.fabMyLocation.setAttribute('aria-pressed', 'false')
    container.append(this.fabMyLocation)

    this.fabGuidance = button(
      'guidance',
      t.t('guidance_mode'),
      'fab',
      callbacks.onGuidance,
    )
    this.fabGuidance.classList.add('anchor-guidance')
    this.fabGuidance.setAttribute('aria-pressed', 'false')
    container.append(this.fabGuidance)

    this.compass = document.createElement('button')
    this.compass.type = 'button'
    this.compass.className = 'anchor-compass'
    this.compass.title = t.t('compass_reset_north')
    this.compass.setAttribute('aria-label', t.t('compass_reset_north'))
    this.compass.addEventListener('click', callbacks.onCompass)
    this.#compassDial = createCompassDial()
    this.compass.append(this.#compassDial)
    container.append(this.compass)

    const mapButtons = document.createElement('div')
    mapButtons.className = 'map-buttons'

    this.fabSatellite = button(
      'layers',
      t.t('satellite_view'),
      'fab fab--neutral',
      callbacks.onSatellite,
    )
    this.fabSatellite.classList.add('anchor-satellite')
    this.fabSatellite.setAttribute('aria-pressed', 'false')

    this.zoomButtons = document.createElement('div')
    this.zoomButtons.style.display = 'flex'
    this.zoomButtons.style.flexDirection = 'column'
    this.zoomButtons.style.gap = 'var(--fab-gap)'
    this.zoomButtons.append(
      button('zoomIn', t.t('zoom_in'), 'fab fab--neutral', callbacks.onZoomIn),
      button('zoomOut', t.t('zoom_out'), 'fab fab--neutral', callbacks.onZoomOut),
    )

    mapButtons.append(this.fabSatellite, this.zoomButtons)
    container.append(mapButtons)

    // --- Panneaux ---
    this.speedHud = document.createElement('div')
    this.speedHud.className = 'hud hud-speed'
    this.speedHud.hidden = true
    this.#speedValue = document.createElement('span')
    this.#speedValue.className = 'hud-speed__speed'
    const separator = document.createElement('span')
    separator.className = 'hud-speed__separator'
    this.#altitudeValue = document.createElement('span')
    this.#altitudeValue.className = 'hud-speed__altitude'
    this.speedHud.append(this.#speedValue, separator, this.#altitudeValue)
    container.append(this.speedHud)

    this.kmIndicator = document.createElement('button')
    this.kmIndicator.type = 'button'
    this.kmIndicator.className = 'hud hud-km'
    this.kmIndicator.hidden = true
    this.kmIndicator.addEventListener('click', callbacks.onKmIndicator)
    container.append(this.kmIndicator)

    this.profileHud = document.createElement('div')
    this.profileHud.className = 'hud hud-profile'
    this.profileHud.hidden = true
    this.#profileCanvas = document.createElement('canvas')
    this.#profileCanvas.className = 'hud-profile__canvas'
    this.profileHud.append(this.#profileCanvas)
    container.append(this.profileHud)

    this.roadbook = document.createElement('div')
    this.roadbook.className = 'hud hud-roadbook'
    this.roadbook.hidden = true
    container.append(this.roadbook)

    this.alertHud = document.createElement('div')
    this.alertHud.className = 'hud hud-alert'
    this.alertHud.hidden = true
    this.#alertCanvas = document.createElement('canvas')
    this.#alertCanvas.className = 'hud-alert__canvas'
    this.alertHud.append(this.#alertCanvas)
    container.append(this.alertHud)

    this.#scaleBar = document.createElement('div')
    this.#scaleBar.className = 'scale-bar'
    this.#scaleLabel = document.createElement('span')
    this.#scaleLabel.className = 'scale-bar__label'
    this.#scaleTrack = document.createElement('div')
    this.#scaleTrack.className = 'scale-bar__bar'
    this.#scaleBar.append(this.#scaleLabel, this.#scaleTrack)
    container.append(this.#scaleBar)

    this.#observeProfileAnchors()
    this.#observeAlertBox()
    this.#observeKmIndicatorSpace()
  }

  /**
   * Suit la place offerte à l'indicateur kilométrique.
   *
   * Android réajuste la taille depuis `onConfigurationChanged`, c'est-à-dire à
   * la rotation. Ici la place peut aussi changer sans rotation — passage en
   * plein écran, fenêtre redimensionnée sur ordinateur, marges système qui
   * bougent — et le texte n'est reposé qu'au tick, cinq à dix secondes plus
   * tard. Pendant un aperçu, jamais.
   *
   * Ce qui est observé est le conteneur, **pas l'indicateur** : l'ajustement
   * change la taille de sa boîte, et l'observer refermerait la boucle. Sa
   * largeur disponible ne dépend de toute façon que de la fenêtre et des deux
   * boutons, qui bougent avec elle.
   */
  #observeKmIndicatorSpace(): void {
    if (typeof ResizeObserver === 'undefined') return

    new ResizeObserver(() => this.fitKmIndicator()).observe(this.root)
  }

  /**
   * Suit la boîte du radar d'alerte pour la retracer quand elle change.
   *
   * Le radar se redessine sinon au seul tick d'alerte, et cette cadence ne
   * couvre pas tout : pendant un aperçu, le drapeau par panneau court-circuite
   * le tick, et le bitmap étiré par la rotation ou le passage en plein écran y
   * reste indéfiniment. Même en marche normale, il faudrait attendre la
   * seconde suivante. Observer la boîte couvre les deux, et les causes qu'on
   * n'a pas prévues.
   *
   * Ce qui est observé est le conteneur, jamais le canevas : le retracé change
   * la mémoire d'image de ce dernier, et l'observer refermerait la boucle. Le
   * conteneur, lui, est posé par la feuille de style et rien ici n'y touche.
   */
  #observeAlertBox(): void {
    // Même repli que pour les bornes du profil : sans `ResizeObserver`, le
    // radar se retrace au tick suivant comme il le faisait avant.
    if (typeof ResizeObserver === 'undefined') return

    new ResizeObserver(() => this.#drawAlert()).observe(this.alertHud)
  }

  /**
   * Suit les boîtes qui bornent le profil d'altitude.
   *
   * Le placement du profil dépend de ses voisins, et deviner quand ils bougent
   * demandait un déclencheur par cause — rotation, indicateur kilométrique dont
   * les chiffres s'allongent, radar qui paraît. Il en manquait toujours un :
   * pendant un aperçu, les drapeaux par panneau court-circuitent le tick, et le
   * profil gardait les bornes du portrait par-dessus le radar. Observer les
   * bornes elles-mêmes couvre les trois cas d'un coup, et ceux qu'on n'a pas
   * prévus.
   *
   * `ResizeObserver` rapporte aussi le passage à `hidden`, sous la forme d'une
   * boîte nulle : l'apparition et la disparition du radar en relèvent donc,
   * sans rappel qui leur soit propre.
   *
   * Le profil lui-même n'est **pas** observé : c'est lui que le rappel
   * redimensionne, et l'observer refermerait la boucle.
   */
  #observeProfileAnchors(): void {
    // Le repli n'a pas de rappel de remplacement : sans `ResizeObserver`, le
    // profil se replace au relevé suivant comme il le faisait avant. Tous les
    // navigateurs qui font tourner MapLibre l'ont.
    if (typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(() => {
      this.layoutProfileHud()
      this.#drawProfile()
    })

    for (const anchor of [
      this.root,
      this.kmIndicator,
      this.alertHud,
      this.fabMenu,
      this.fabMyLocation,
    ]) {
      observer.observe(anchor)
    }
  }

  /* -------------------------------------------------------------------
   * Vitesse et altitude
   * ---------------------------------------------------------------- */

  setSpeedAltitude(
    speedKmh: number | null,
    altitudeM: number | null,
    options: { visible: boolean; showAltitude: boolean; textSizeLevel: number },
  ): void {
    this.speedHud.hidden = !options.visible
    if (!options.visible) return

    this.speedHud.style.fontSize = `${hudTextSize(options.textSizeLevel)}px`

    this.#speedValue.textContent =
      speedKmh === null
        ? this.t.t('speed_no_data')
        : this.t.t('speed_format', { value: Math.round(speedKmh) })

    this.#altitudeValue.hidden = !options.showAltitude
    this.#altitudeValue.textContent =
      altitudeM === null
        ? this.t.t('altitude_no_data')
        : this.t.t('altitude_format', { value: Math.round(altitudeM) })
  }

  /* -------------------------------------------------------------------
   * Indicateur kilométrique
   * ---------------------------------------------------------------- */

  /**
   * Position, distance restante et total.
   *
   * Les valeurs sont **tronquées** et non arrondies : afficher « 12,4 km »
   * alors qu'on est à 12,39 donnerait l'impression d'avoir déjà passé le
   * point.
   */
  setKmIndicator(
    currentKm: number | null,
    remainingKm: number,
    totalKm: number,
  ): void {
    if (currentKm === null) {
      this.kmIndicator.hidden = true
      return
    }

    this.kmIndicator.hidden = false
    const truncate = (value: number): string =>
      this.t.number(truncateKm(value), 1)

    // Le restant ne descend pas sous zéro : passé l'arrivée et jusqu'à la
    // tolérance de 50 m, l'indicateur affichait « -0,0 ». `coerceAtLeast(0.0)`
    // chez Android (`LiveHudController.kt:216`).
    // Le séparateur est un trait horizontal épais (U+2501), comme lui.
    this.kmIndicator.textContent =
      `${truncate(currentKm)} ━ ${truncate(Math.max(remainingKm, 0))} ━ ${truncate(totalKm)}`

    this.fitKmIndicator()
  }

  /**
   * Ajuste la taille du texte de l'indicateur à la place entre les boutons.
   *
   * Android n'essaie les tailles réduites qu'en portrait — en paysage
   * l'indicateur est calé à gauche et la largeur ne manque jamais. Sans cet
   * ajustement, une trace de plus de mille kilomètres écrit quatre chiffres
   * trois fois et passe sous les deux boutons du haut.
   *
   * La place disponible est **mesurée** entre les deux boutons plutôt que
   * déduite de leurs dimensions. Le calcul d'Android — largeur d'écran moins
   * deux fois 72 dp moins 32 dp — donne le même nombre sur une mise en page
   * ordinaire, mais le déduire ici demanderait d'y recopier `--fab-size`, la
   * marge de bord et l'écart : trois constantes à tenir en phase avec la
   * feuille de style, pour un résultat que le navigateur sait donner.
   */
  fitKmIndicator(): void {
    if (this.kmIndicator.hidden) return

    // La taille vit sur `:root` et non sur l'élément : `--hud-km-height` en
    // découle, et le profil d'altitude comme la boussole s'y accrochent. Posée
    // plus bas dans l'arbre, elle laisserait cette hauteur figée sur 22 px.
    const root = document.documentElement

    if (!matchMedia('(orientation: landscape)').matches) {
      const available = this.#kmIndicatorAvailableWidth()
      const size = fitKmIndicatorFontSize(available, (fontSize) => {
        root.style.setProperty('--hud-km-font', `${fontSize}px`)
        return this.#kmIndicatorTextWidth()
      })
      root.style.setProperty('--hud-km-font', `${size}px`)
      return
    }

    root.style.setProperty(
      '--hud-km-font',
      `${KM_INDICATOR_FONT_SIZES[0]}px`,
    )
  }

  /** Place laissée à l'indicateur entre le bouton de menu et celui de localisation. */
  #kmIndicatorAvailableWidth(): number {
    const menu = this.fabMenu.getBoundingClientRect()
    const myLocation = this.fabMyLocation.getBoundingClientRect()

    // Le jeu de 32 px est celui d'Android, qui le retranche de la largeur
    // d'écran : l'indicateur ne doit pas venir au contact des boutons.
    return myLocation.left - menu.right - KM_INDICATOR_CLEARANCE
  }

  /**
   * Largeur du texte réellement dessiné, sans le fond du panneau.
   *
   * C'est le texte qu'Android mesure, pas la vue : lire la boîte de l'élément
   * y ajouterait ses marges intérieures, et ferait réduire la police une
   * vingtaine de pixels trop tôt.
   */
  #kmIndicatorTextWidth(): number {
    const range = document.createRange()
    range.selectNodeContents(this.kmIndicator)
    return range.getBoundingClientRect().width
  }

  /* -------------------------------------------------------------------
   * Roadbook
   * ---------------------------------------------------------------- */

  setRoadbook(entries: readonly RoadbookEntry[], textSizeLevel: number): void {
    if (entries.length === 0) {
      this.roadbook.hidden = true
      this.roadbook.replaceChildren()
      return
    }

    this.roadbook.hidden = false
    this.roadbook.style.fontSize = `${hudTextSize(textSizeLevel)}px`

    const rows = entries.map((entry) => {
      const row = document.createElement('div')
      row.className = 'roadbook-entry'
      // Un marqueur dépassé reste affiché un instant, mais grisé.
      row.dataset.passed = String(entry.remainingKm < 0)

      const symbol = document.createElement('span')
      symbol.className = 'roadbook-entry__symbol'
      symbol.textContent = entry.marker.symbol

      const texts = document.createElement('span')
      texts.className = 'roadbook-entry__texts'

      const km = document.createElement('span')
      km.textContent = `${this.t.number(truncateKm(entry.marker.distanceKm), 1)}`

      const remaining = document.createElement('span')
      remaining.className = 'roadbook-entry__remaining'
      remaining.textContent = `${this.t.number(truncateKm(entry.remainingKm), 1)}`

      texts.append(km, remaining)
      row.append(symbol, texts)
      return row
    })

    this.roadbook.replaceChildren(...rows)
  }

  /* -------------------------------------------------------------------
   * Profil d'altitude
   * ---------------------------------------------------------------- */

  /**
   * Trace le profil dans la fenêtre courante.
   *
   * L'amplitude verticale ne descend jamais sous 2 % de la distance affichée :
   * sans ce plancher, un faux plat apparaîtrait comme un col.
   */
  /**
   * Étale le profil d'altitude sur ce que ses voisins lui laissent.
   *
   * Portage du positionnement adaptatif d'`ElevationProfileHudView` : le
   * panneau occupe l'espace entre l'indicateur kilométrique et le bouton de
   * localisation, et se resserre entre l'indicateur et le radar d'alerte quand
   * celui-ci est affiché. Une largeur écrite dans la feuille de style ne peut
   * pas suivre : elle ignore la longueur du texte de l'indicateur, qui change
   * avec les kilomètres, et l'apparition du radar, qui va et vient en roulant.
   *
   * Le voisinage se décide au **chevauchement vertical**, ce qui donne les deux
   * dispositions sans distinguer les orientations : en paysage l'indicateur est
   * à côté du profil et le borne à gauche, en portrait il est au-dessus et
   * c'est le bouton de menu qui borne.
   */
  layoutProfileHud(): void {
    const profile = this.profileHud
    if (profile.hidden) return

    const root = this.root.getBoundingClientRect()
    const band = profile.getBoundingClientRect()
    if (band.height === 0) return

    const beside = (element: HTMLElement): DOMRect | null => {
      if (element.hidden) return null
      const rect = element.getBoundingClientRect()
      if (rect.width === 0) return null
      return rect.bottom > band.top && rect.top < band.bottom ? rect : null
    }

    const leftAnchor = beside(this.kmIndicator) ?? this.fabMenu.getBoundingClientRect()
    // Le radar prime sur le bouton : il est plus à gauche, donc plus
    // contraignant, et c'est lui qui disparaît en cours de route.
    const rightAnchor =
      beside(this.alertHud) ?? this.fabMyLocation.getBoundingClientRect()

    profile.style.left = `${leftAnchor.right - root.left + PROFILE_GAP}px`
    profile.style.right = `${root.right - rightAnchor.left + PROFILE_GAP}px`
    profile.style.width = 'auto'
    profile.style.transform = 'none'
  }

  setElevationProfile(
    distances: readonly number[],
    elevations: readonly number[],
    windowStart: number,
    windowEnd: number,
    cursorFraction: number,
    visible: boolean,
  ): void {
    this.profileHud.hidden = !visible
    if (!visible || distances.length < 2) return

    this.#profileFrame = {
      distances,
      elevations,
      windowStart,
      windowEnd,
      cursorFraction,
    }

    // Avant toute mesure du canevas : la largeur du panneau dépend de ses
    // voisins, et la lire avant de l'avoir posée dessinerait à l'ancienne.
    this.layoutProfileHud()
    this.#drawProfile()
  }

  /**
   * Retrace la dernière image du profil à la largeur courante du panneau.
   *
   * Séparé de `setElevationProfile` parce que l'observateur des bornes n'a pas
   * de données nouvelles à poser : ce qui change est la place, et c'est la même
   * courbe qu'il faut redessiner à la nouvelle échelle.
   */
  #drawProfile(): void {
    const frame = this.#profileFrame
    if (frame === null || this.profileHud.hidden) return

    const { distances, elevations, windowStart, windowEnd, cursorFraction } =
      frame

    const canvas = this.#profileCanvas
    const ratio = canvasPixelRatio()
    const width = canvas.clientWidth
    const height = canvas.clientHeight
    if (width === 0 || height === 0) return

    canvas.width = Math.round(width * ratio)
    canvas.height = Math.round(height * ratio)

    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0)
    ctx.clearRect(0, 0, width, height)

    const span = windowEnd - windowStart
    if (span <= 0) return

    let minEle = Infinity
    let maxEle = -Infinity
    for (const ele of elevations) {
      if (ele < minEle) minEle = ele
      if (ele > maxEle) maxEle = ele
    }

    const GRADE_THRESHOLD = 0.02
    const eleRange = Math.max(maxEle - minEle, span * GRADE_THRESHOLD, 10)
    const eleBase = (minEle + maxEle) / 2 - eleRange / 2

    const x = (distance: number): number =>
      ((distance - windowStart) / span) * width
    const y = (ele: number): number => height - ((ele - eleBase) / eleRange) * height

    // Graduations aux kilomètres entiers, en distance absolue sur la trace.
    ctx.strokeStyle = 'rgba(255,255,255,0.25)'
    ctx.lineWidth = 1
    for (
      let km = Math.ceil(windowStart / 1000);
      km * 1000 <= windowEnd;
      km++
    ) {
      const tickX = Math.round(x(km * 1000)) + 0.5
      ctx.beginPath()
      ctx.moveTo(tickX, 0)
      ctx.lineTo(tickX, height)
      ctx.stroke()
    }

    ctx.save()
    ctx.beginPath()
    ctx.rect(0, 0, width, height)
    ctx.clip()

    ctx.beginPath()
    ctx.moveTo(x(distances[0]!), height)
    for (let i = 0; i < distances.length; i++) {
      ctx.lineTo(x(distances[i]!), y(elevations[i]!))
    }
    ctx.lineTo(x(distances[distances.length - 1]!), height)
    ctx.closePath()
    ctx.fillStyle = 'rgba(255,255,255,0.25)'
    ctx.fill()

    ctx.beginPath()
    for (let i = 0; i < distances.length; i++) {
      const px = x(distances[i]!)
      const py = y(elevations[i]!)
      if (i === 0) ctx.moveTo(px, py)
      else ctx.lineTo(px, py)
    }
    ctx.strokeStyle = '#FFFFFF'
    ctx.lineWidth = 2
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.stroke()
    ctx.restore()

    // Curseur de position, rouge vif pour rester repérable d'un coup d'œil.
    const cursorX = Math.round(cursorFraction * width) + 0.5
    ctx.strokeStyle = '#FF0000'
    ctx.lineWidth = 2
    ctx.beginPath()
    ctx.moveTo(cursorX, 0)
    ctx.lineTo(cursorX, height)
    ctx.stroke()
  }

  /* -------------------------------------------------------------------
   * Radar d'alerte
   * ---------------------------------------------------------------- */

  /**
   * Barre verticale des marqueurs à venir.
   *
   * La couleur du contour va du vert au rouge à mesure qu'on approche : la
   * distance se lit sans avoir à déchiffrer un nombre.
   */
  setMarkerAlert(
    entries: readonly RoadbookEntry[],
    alertRadiusKm: number,
    sizeLevel: number,
    visible: boolean,
  ): void {
    // Le radar borne le profil à droite, mais rien n'est rappelé ici : c'est
    // l'observateur des bornes qui voit sa boîte apparaître et disparaître.
    // Un rappel de plus serait un second déclencheur à tenir à jour.
    this.alertHud.hidden = !visible || entries.length === 0

    if (this.alertHud.hidden) return

    this.#alertFrame = { entries, alertRadiusKm, sizeLevel }
    this.#drawAlert()
  }

  /**
   * Retrace la dernière image du radar à la taille courante de sa boîte.
   *
   * Séparé de `setMarkerAlert` pour la même raison que `#drawProfile` l'est de
   * `setElevationProfile` : l'observateur n'a pas d'entrées nouvelles à poser,
   * ce qui a changé est la place.
   */
  #drawAlert(): void {
    const frame = this.#alertFrame
    if (frame === null || this.alertHud.hidden) return

    const { entries, alertRadiusKm, sizeLevel } = frame

    const canvas = this.#alertCanvas
    const ratio = canvasPixelRatio()
    const width = canvas.clientWidth
    const height = canvas.clientHeight
    if (width === 0 || height === 0) return

    canvas.width = Math.round(width * ratio)
    canvas.height = Math.round(height * ratio)

    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0)
    ctx.clearRect(0, 0, width, height)

    const symbolSize = markerAlertSize(sizeLevel)
    const centerX = width / 2
    const navSize = symbolSize
    const navY = height - navSize * 0.9
    const zoneTop = symbolSize * 0.6
    const zoneHeight = navY - zoneTop

    // Axe et graduations
    ctx.strokeStyle = 'rgba(255,255,255,0.25)'
    ctx.lineWidth = 2
    ctx.beginPath()
    ctx.moveTo(centerX, zoneTop)
    ctx.lineTo(centerX, navY)
    ctx.stroke()

    for (let km = 0; km <= Math.floor(alertRadiusKm); km++) {
      const ratioY = 1 - km / alertRadiusKm
      const tickY = Math.round(zoneTop + ratioY * zoneHeight) + 0.5
      ctx.setLineDash([])
      ctx.beginPath()
      ctx.moveTo(centerX - width / 2 + 2, tickY)
      ctx.lineTo(centerX + width / 2 - 2, tickY)
      ctx.stroke()

      // Demi-kilomètres en pointillé, pour densifier sans surcharger.
      const halfRatio = 1 - (km + 0.5) / alertRadiusKm
      if (halfRatio >= 0) {
        const halfY = Math.round(zoneTop + halfRatio * zoneHeight) + 0.5
        ctx.setLineDash([4, 4])
        ctx.beginPath()
        ctx.moveTo(centerX - width / 4, halfY)
        ctx.lineTo(centerX + width / 4, halfY)
        ctx.stroke()
      }
    }
    ctx.setLineDash([])

    for (const entry of entries) {
      const passed = entry.remainingKm < 0
      const clamped = Math.min(Math.max(entry.remainingKm, 0), alertRadiusKm)
      const proximity = 1 - clamped / alertRadiusKm
      const centerY = passed
        ? navY + navSize * 0.7
        : zoneTop + proximity * zoneHeight

      drawDiamond(ctx, centerX, centerY, symbolSize, entry.marker.symbol, {
        passed,
        proximity,
      })
    }

    drawNavigationPuck(ctx, centerX, navY, navSize)
  }

  /* -------------------------------------------------------------------
   * Boussole et échelle
   * ---------------------------------------------------------------- */

  setCompassBearing(bearing: number): void {
    // Redessiner sous le degré n'apporte rien de perceptible.
    if (Math.abs(bearing - this.#lastCompassBearing) < 1) return
    this.#lastCompassBearing = bearing
    this.#compassDial.style.transform = `rotate(${-bearing}deg)`
  }

  /**
   * Met à jour la barre d'échelle.
   *
   * Le pas est choisi dans une suite « ronde » : une échelle affichant
   * « 137 m » serait exacte mais inutilisable pour estimer une distance.
   */
  setScale(metersPerPixel: number, maxWidthPx = 150): void {
    const steps = [
      5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000, 10_000, 20_000, 50_000,
      100_000, 200_000, 500_000, 1_000_000,
    ]

    const maxMeters = metersPerPixel * maxWidthPx
    let chosen = steps[0]!
    for (const step of steps) {
      if (step <= maxMeters) chosen = step
      else break
    }

    const widthPx = chosen / metersPerPixel
    this.#scaleTrack.style.width = `${Math.round(widthPx)}px`
    this.#scaleLabel.textContent =
      chosen >= 1000 ? `${chosen / 1000} km` : `${chosen} m`
  }

  /* -------------------------------------------------------------------
   * Messages
   * ---------------------------------------------------------------- */

  /** Message bref, avec action facultative. */
  showSnackbar(
    message: string,
    action?: { label: string; onClick: () => void },
    durationMs = 4000,
  ): () => void {
    this.#snackbar?.remove()

    const bar = document.createElement('div')
    bar.className = 'snackbar'
    bar.setAttribute('role', 'status')

    const text = document.createElement('span')
    text.textContent = message
    bar.append(text)

    if (action) {
      const button = document.createElement('button')
      button.type = 'button'
      button.className = 'snackbar__action'
      button.textContent = action.label
      button.addEventListener('click', () => {
        action.onClick()
        dismiss()
      })
      bar.append(button)
    }

    this.root.append(bar)
    this.#snackbar = bar

    const dismiss = (): void => {
      window.clearTimeout(timer)
      bar.remove()
      if (this.#snackbar === bar) this.#snackbar = null
    }

    // Une action attend une décision : le message reste jusqu'au choix.
    const timer = action ? 0 : window.setTimeout(dismiss, durationMs)

    return dismiss
  }
}

/* ---------------------------------------------------------------------
 * Dessins auxiliaires
 * ------------------------------------------------------------------ */

function createCompassDial(): SVGSVGElement {
  const ns = 'http://www.w3.org/2000/svg'
  const svg = document.createElementNS(ns, 'svg')
  svg.setAttribute('viewBox', '0 0 40 40')
  svg.setAttribute('class', 'compass__dial')
  svg.setAttribute('aria-hidden', 'true')

  const disc = document.createElementNS(ns, 'circle')
  disc.setAttribute('cx', '20')
  disc.setAttribute('cy', '20')
  disc.setAttribute('r', '18')
  disc.setAttribute('fill', '#FFFFFF')
  disc.setAttribute('stroke', '#9E9E9E')
  disc.setAttribute('stroke-width', '1.5')

  // Aiguille nord en rouge, sud en gris : convention universelle.
  const north = document.createElementNS(ns, 'path')
  north.setAttribute('d', 'M20,8.3 L14.6,20 L25.4,20 Z')
  north.setAttribute('fill', '#E53935')

  const south = document.createElementNS(ns, 'path')
  south.setAttribute('d', 'M20,31.7 L14.6,20 L25.4,20 Z')
  south.setAttribute('fill', '#BDBDBD')

  svg.append(disc, north, south)
  return svg
}

function drawDiamond(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  size: number,
  symbol: string,
  state: { passed: boolean; proximity: number },
): void {
  const half = size / 2
  const inset = size * 0.05

  ctx.beginPath()
  ctx.moveTo(centerX, centerY - half + inset)
  ctx.lineTo(centerX + half - inset, centerY)
  ctx.lineTo(centerX, centerY + half - inset)
  ctx.lineTo(centerX - half + inset, centerY)
  ctx.closePath()

  ctx.fillStyle = state.passed ? 'rgba(255,255,255,0.5)' : '#FFFFFF'
  ctx.fill()

  ctx.strokeStyle = state.passed
    ? 'rgba(255,255,255,0.5)'
    : proximityColor(state.proximity)
  ctx.lineWidth = size / 10
  ctx.lineJoin = 'round'
  ctx.stroke()

  ctx.fillStyle = state.passed ? 'rgba(0,0,0,0.5)' : '#000000'
  ctx.font = `bold ${size * 0.38}px system-ui, sans-serif`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(symbol, centerX, centerY)
}

/**
 * Vert au loin, jaune à mi-distance, rouge à l'approche.
 *
 * Le point de bascule à mi-parcours est volontairement asymétrique : la
 * seconde moitié parcourt deux fois plus de teinte, pour que l'alerte se
 * distingue nettement quand le marqueur est proche.
 */
function proximityColor(proximity: number): string {
  const hue =
    proximity <= 0.5 ? 120 - proximity * 120 : 60 - (proximity - 0.5) * 120
  const clamped = Math.min(Math.max(hue, 0), 120)
  return `hsl(${clamped}, 80%, 45%)`
}

function drawNavigationPuck(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  size: number,
): void {
  ctx.fillStyle = '#FFFFFF'
  ctx.beginPath()
  ctx.arc(centerX, centerY, size * 0.4, 0, Math.PI * 2)
  ctx.fill()

  ctx.fillStyle = '#4285F4'
  ctx.beginPath()
  ctx.moveTo(centerX, centerY - size * 0.25)
  ctx.lineTo(centerX - size * 0.22, centerY + size * 0.22)
  ctx.lineTo(centerX, centerY + size * 0.08)
  ctx.lineTo(centerX + size * 0.22, centerY + size * 0.22)
  ctx.closePath()
  ctx.fill()
}
